// filepath: /Users/shayan/MySite-UserProfile/Code/src/HelperComponents/form/components/DateField.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { addDays } from "../../../Utility/Utility";

type DateFieldProps = {
  id: string;
  placeholder?: string;
  value?: any;
  minDays?: string;
  maxDays?: string;
  onChange: (date?: Date) => void;
};

export const formatDateInputValue = (date?: Date): string => {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}/${date.getFullYear()}`;
};

export const parseDateInputValue = (value: string): Date | undefined => {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return undefined;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
};

const DateField: React.FC<DateFieldProps> = ({
  id,
  placeholder,
  value,
  minDays,
  maxDays,
  onChange,
}) => {
  const toDate = (v?: any): Date | undefined => {
    if (!v) return undefined;
    // If value is a date-only string (YYYY-MM-DD), construct a local date to avoid UTC shifting
    if (typeof v === "string") {
      // Extract YYYY-MM-DD portion from ISO strings like "2026-04-20T05:00:00.000Z"
      const m = v.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) {
        const datePart = m[0]; // e.g., "2026-04-20"
        const [yearStr, monthStr, dayStr] = datePart.split("-");
        const year = Number(yearStr);
        const month = Number(monthStr) - 1; // 0-based
        const day = Number(dayStr);
        const d = new Date();
        d.setFullYear(year, month, day);
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  };
  const parsed = useMemo(() => toDate(value), [value]);

  const minDate = useMemo(
    () => (minDays ? addDays(new Date(), parseInt(minDays, 10)) : undefined),
    [minDays],
  );
  const maxDate = useMemo(
    () => (maxDays ? addDays(new Date(), parseInt(maxDays, 10)) : undefined),
    [maxDays],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    formatDateInputValue(parsed),
  );
  const [inputError, setInputError] = useState("");

  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const base = parsed ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  useEffect(() => {
    setInputValue(formatDateInputValue(parsed));
    setInputError("");
    if (parsed) {
      setCurrentMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
    }
  }, [parsed]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const isSameDay = (a?: Date, b?: Date): boolean => {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const isBefore = (a: Date, b: Date): boolean => {
    return a.getTime() < b.getTime();
  };

  const isAfter = (a: Date, b: Date): boolean => {
    return a.getTime() > b.getTime();
  };

  const startOfDay = (d: Date): Date => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };

  const days = useMemo(() => {
    const monthStart = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1,
    );
    const startWeekday = monthStart.getDay(); // 0 (Sun) - 6 (Sat)
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - startWeekday);

    const cells: {
      date: Date;
      inCurrentMonth: boolean;
      disabled: boolean;
    }[] = [];

    const minBound = minDate ? startOfDay(minDate) : undefined;
    const maxBound = maxDate ? startOfDay(maxDate) : undefined;

    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      const inCurrentMonth = d.getMonth() === currentMonth.getMonth();

      let disabled = false;
      const dayStart = startOfDay(d);
      if (minBound && isBefore(dayStart, minBound)) {
        disabled = true;
      }
      if (maxBound && isAfter(dayStart, maxBound)) {
        disabled = true;
      }

      cells.push({ date: d, inCurrentMonth, disabled });
    }

    return cells;
  }, [currentMonth, minDate, maxDate]);

  const handleSelectDate = (d: Date) => {
    const selectedDate = startOfDay(d);
    setInputValue(formatDateInputValue(selectedDate));
    setInputError("");
    onChange(selectedDate);
    setOpen(false);
  };

  const commitTypedDate = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      setInputError("");
      onChange(undefined);
      return;
    }

    const typedDate = parseDateInputValue(trimmed);
    if (!typedDate) {
      setInputError("Enter a valid date in MM/DD/YYYY format.");
      return;
    }

    const minBound = minDate ? startOfDay(minDate) : undefined;
    const maxBound = maxDate ? startOfDay(maxDate) : undefined;
    if (minBound && isBefore(typedDate, minBound)) {
      setInputError(`Date must be on or after ${formatDateInputValue(minBound)}.`);
      return;
    }
    if (maxBound && isAfter(typedDate, maxBound)) {
      setInputError(`Date must be on or before ${formatDateInputValue(maxBound)}.`);
      return;
    }

    setInputValue(formatDateInputValue(typedDate));
    setInputError("");
    setCurrentMonth(
      new Date(typedDate.getFullYear(), typedDate.getMonth(), 1),
    );
    onChange(typedDate);
  };

  const handleSelectToday = () => {
    let today = startOfDay(new Date());
    const minBound = minDate ? startOfDay(minDate) : undefined;
    const maxBound = maxDate ? startOfDay(maxDate) : undefined;

    if (minBound && isBefore(today, minBound)) {
      today = minBound;
    }
    if (maxBound && isAfter(today, maxBound)) {
      today = maxBound;
    }

    handleSelectDate(today);
  };

  const goPrevMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const goNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const monthLabel = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(undefined, {
      month: "long",
      year: "numeric",
    });
    return formatter.format(currentMonth);
  }, [currentMonth]);

  const weekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  return (
    <div className="date-field-wrapper" ref={containerRef}>
      <div className="form-select date-field-control">
        <input
          id={id}
          className="date-field-input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder || "MM/DD/YYYY"}
          value={inputValue}
          aria-invalid={!!inputError}
          aria-describedby={inputError ? `${id}-date-error` : undefined}
          onChange={(event) => {
            const nextValue = event.target.value;
            setInputValue(nextValue);
            setInputError("");
            if (!nextValue.trim()) onChange(undefined);
          }}
          onBlur={() => commitTypedDate(inputValue)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitTypedDate(inputValue);
            }
          }}
        />
        <button
          type="button"
          className="date-field-calendar-button"
          aria-label="Open date picker"
          aria-haspopup="dialog"
          aria-expanded={open}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="date-field-calendar-icon" aria-hidden="true">
            📅
          </span>
        </button>
      </div>
      {inputError && (
        <div id={`${id}-date-error`} className="date-field-error" role="alert">
          {inputError}
        </div>
      )}

      {open && (
        <div
          className="date-picker-popup"
          role="dialog"
          aria-modal="false"
          aria-label="Choose date"
        >
          <div className="date-picker-header">
            <button
              type="button"
              className="date-picker-nav-btn"
              onClick={goPrevMonth}
            >
              ‹
            </button>
            <div className="date-picker-month-label">{monthLabel}</div>
            <button
              type="button"
              className="date-picker-nav-btn"
              onClick={goNextMonth}
            >
              ›
            </button>
          </div>
          <div className="date-picker-weekdays">
            {weekdayLabels.map((w) => (
              <div key={w} className="date-picker-weekday">
                {w}
              </div>
            ))}
          </div>
          <div className="date-picker-grid">
            {days.map(({ date, inCurrentMonth, disabled }) => {
              const isSelected = isSameDay(date, parsed);
              const isToday = isSameDay(date, new Date());
              const dayNumber = date.getDate();

              const classes = ["date-picker-day-btn"];
              if (!inCurrentMonth) classes.push("date-picker-day-btn--muted");
              if (disabled) classes.push("date-picker-day-btn--disabled");
              if (isSelected) classes.push("date-picker-day-btn--selected");
              if (isToday && !isSelected)
                classes.push("date-picker-day-btn--today");

              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  className={classes.join(" ")}
                  onClick={() => !disabled && handleSelectDate(date)}
                  disabled={disabled}
                >
                  {dayNumber}
                </button>
              );
            })}
          </div>
          <div className="date-picker-footer">
            <button
              type="button"
              className="date-picker-today-btn"
              onClick={handleSelectToday}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateField;
